import React, { useContext, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Checkbox,
    ClickAwayListener,
    Divider,
    Drawer,
    FormControlLabel,
    IconButton,
    InputBase,
    MenuItem,
    Select,
    Snackbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EastIcon from '@mui/icons-material/East';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import HandoutImageLoader from '../handout/handout-image-loader';
import handoutsFilterIcon from '../../../assets/icons/HandoutsFilter.svg';
import diagramsFilterIcon from '../../../assets/icons/DiagramsFilter.svg';
import models3dFilterIcon from '../../../assets/icons/Models3dFilter.svg';
import customiseIcon from '../../../assets/icons/CustomiseHandout.svg';
import addToBundleIcon from '../../../assets/icons/AddToBundle.svg';
import shareIcon from '../../../assets/icons/ShareEnvelop.svg';
import arrowRightIcon from '../../../assets/icons/Arrow-white_Right.svg';
import arrowRightPinkIcon from '../../../assets/icons/ArrowRight.svg';
import filtersIcon from '../../../assets/icons/FIlters.svg';
import UserApi from '../../../rest-api/user-api';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import { getCTAUpgradeImageUrl } from '../../../config';
import QuickShareDialog from '../handout/quick-share-dialog';
import { getHandoutBundleUpgradeTarget } from '../../../utils/subscription-plan-utils';
import SuggestHandoutDialog from './suggest-handout-dialog';

const FORMAT_KEYS = {
    HANDOUT: 'handout',
    DIAGRAM: 'diagram',
    MODEL_3D: 'model3d',
};

const FORMAT_OPTIONS = [
    { key: FORMAT_KEYS.HANDOUT, label: 'Handouts', icon: handoutsFilterIcon, iconWidth: 22, iconHeight: 28 },
    { key: FORMAT_KEYS.DIAGRAM, label: 'Diagrams', icon: diagramsFilterIcon, iconWidth: 32, iconHeight: 36 },
    { key: FORMAT_KEYS.MODEL_3D, label: '3D Models', icon: models3dFilterIcon, iconWidth: 31, iconHeight: 28 },
];

const SPECIES_OPTIONS = ['Canine', 'Feline', 'Equine', 'Small Animals', 'Farm Animals', 'Exotic Animals'];
const CONDITION_CATEGORY_OPTIONS = [
    'Behaviour',
    'Dentistry',
    'Dermatology',
    'Emergency',
    'Farm Animals',
    'General anatomy',
    'General principles',
    'Internal medicine',
    'Neurology',
    'Ophthalmology',
    'Orthopaedics',
    'Parasites',
    'Reproduction',
    'Surgery',
];

const INITIAL_VISIBLE_CONDITION_FILTERS = 5;
const RECENT_CARDS_PER_VIEW = 3;
const MOBILE_RECENT_CARDS_PER_VIEW = 1;
const RECENT_SEARCHES_LIMIT = 5;
const DESKTOP_ITEMS_PER_PAGE_OPTIONS = [6, 8, 12, 24];
const MOBILE_ITEMS_PER_PAGE_OPTIONS = [6, 9, 12, 25];

const normalize = (value) => (value || '').toString().trim().toLowerCase();

// Match the production alphabetical ordering by sorting on the display-independent handout name.
const compareAlphabeticalContent = (contentStore, a, b) => {
    const getSortableName = (item) =>
        (contentStore.getSortableName ? contentStore.getSortableName(item) : item.contentName) || '';
    const sortableCompare = getSortableName(a).localeCompare(getSortableName(b));
    if (sortableCompare !== 0) return sortableCompare;
    return (a.contentName || '').localeCompare(b.contentName || '');
};

const deepContains = (value, target) => {
    const targetNormalized = normalize(target);
    if (!targetNormalized) return false;

    if (Array.isArray(value)) return value.some((entry) => deepContains(entry, targetNormalized));
    if (value && typeof value === 'object') {
        return Object.entries(value).some(
            ([key, nestedValue]) => normalize(key) === targetNormalized || deepContains(nestedValue, targetNormalized),
        );
    }
    if (typeof value === 'string') return normalize(value) === targetNormalized;
    return false;
};

const getPaginationItems = (currentPage, pageCount) => {
    if (pageCount <= 5) return Array.from({ length: pageCount }, (_, index) => index + 1);
    if (currentPage <= 3) return [1, 2, 3, 'ellipsis-end', pageCount];
    if (currentPage >= pageCount - 2) return [1, 'ellipsis-start', pageCount - 2, pageCount - 1, pageCount];
    return [1, 'ellipsis-start', currentPage, 'ellipsis-end', pageCount];
};

const getFormatKey = (contentItem) => {
    const categories = contentItem?.categories || {};
    if (deepContains(categories, '3D Models')) return FORMAT_KEYS.MODEL_3D;
    if (deepContains(categories, 'Diagrams')) return FORMAT_KEYS.DIAGRAM;
    return FORMAT_KEYS.HANDOUT;
};

const getFormatOption = (contentItem) => {
    const formatKey = getFormatKey(contentItem);
    return FORMAT_OPTIONS.find((option) => option.key === formatKey) || FORMAT_OPTIONS[0];
};

const getPreviewSrc = (contentItem) => {
    const localeData = contentItem?.contentData?.['en-UK'] || [];
    const allBlocks = localeData.flatMap((section) => section.blocks || []);
    const modelBlock = allBlocks.find((block) => block.type === '3dModel');
    if (modelBlock?.imageSrc) return modelBlock.imageSrc;
    const illustration = allBlocks.find((block) => block.type === 'illustration');
    return illustration?.src || null;
};

const isPure3DModelHandout = (contentItem) => {
    const localeData = contentItem?.contentData?.['en-UK'] || [];
    if (!Array.isArray(localeData) || localeData.length === 0) return false;
    const allBlocks = localeData.flatMap((section) => section.blocks || []);
    if (!allBlocks.some((block) => block.type === '3dModel')) return false;
    return allBlocks.every((block) => block.type === '3dModel' || block.type === 'documentTitle');
};

const FilterSection = ({
    title,
    options,
    selectedValues,
    onToggleValue,
    expanded,
    onToggleExpanded,
    showAll = true,
    onToggleShowAll,
}) => {
    const displayedOptions = showAll ? options : options.slice(0, INITIAL_VISIBLE_CONDITION_FILTERS);

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    mb: 1,
                }}
                onClick={onToggleExpanded}
            >
                <Typography sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: '#000453' }}>
                    {title}
                </Typography>
                {expanded ? (
                    <KeyboardArrowDownIcon sx={{ color: '#000453' }} />
                ) : (
                    <KeyboardArrowUpIcon sx={{ color: '#000453' }} />
                )}
            </Box>

            {expanded && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {displayedOptions.map((option) => (
                        <FormControlLabel
                            key={option.value || option.label}
                            sx={{ margin: 0, minHeight: 28 }}
                            control={
                                <Checkbox
                                    checked={selectedValues.includes(option.value || option.label)}
                                    onChange={() => onToggleValue(option.value || option.label)}
                                    sx={{
                                        color: '#000453',
                                        padding: '4px 8px 4px 0',
                                        '& .MuiSvgIcon-root': { fontSize: 24 },
                                        '&.Mui-checked': { color: '#FF669B' },
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: '16px', lineHeight: '24px', color: '#000453' }}>
                                    {option.label} ({option.count})
                                </Typography>
                            }
                        />
                    ))}
                    {!showAll && options.length > INITIAL_VISIBLE_CONDITION_FILTERS && (
                        <Box
                            component="button"
                            onClick={onToggleShowAll}
                            sx={{
                                marginTop: '4px',
                                border: 'none',
                                background: 'transparent',
                                color: '#000453',
                                textAlign: 'left',
                                padding: 0,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '16px',
                                lineHeight: '24px',
                            }}
                        >
                            Show more
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

const FeaturedCard = ({ item, imageStore, navigate, onAddToBundle, onQuickShare, onCustomise }) => {
    const isPure3D = isPure3DModelHandout(item);

    return (
        <Box
            onClick={() => navigate(`/handout/${item.contentId}`)}
            sx={{
                width: { xs: '100%', md: 314 },
                height: 414,
                background: '#F0F0FC',
                borderRadius: '20px',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                minWidth: 0,
                overflow: 'hidden',
                border: '1px solid #E5E5F3',
            }}
        >
            <Box
                sx={{
                    mx: '14px',
                    mt: '14px',
                    height: 256,
                    borderRadius: '10px',
                    background: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                }}
            >
                <HandoutImageLoader
                    src={getPreviewSrc(item)}
                    alt={item.contentName}
                    imageStore={imageStore}
                    handoutData={item}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                />
            </Box>
            <Box sx={{ px: '14px', pt: '10px', pb: '14px', flexShrink: 0 }}>
                <Typography
                    sx={{
                        color: '#000453',
                        fontWeight: 600,
                        fontSize: '22px',
                        lineHeight: '26px',
                        letterSpacing: '-0.44px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        minHeight: '52px',
                    }}
                >
                    {item.contentName}
                </Typography>
                <Box
                    sx={{
                        mt: '10px',
                        height: '2px',
                        background: 'rgba(0, 4, 83, 0.10)',
                        borderRadius: '1px',
                        flexShrink: 0,
                    }}
                />
                <Box
                    sx={{
                        mt: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexShrink: 0,
                    }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Tooltip
                        title={isPure3D ? 'Customise unavailable for pure 3D model handouts' : 'Open in customise mode'}
                        arrow
                    >
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                background: isPure3D ? '#FF669B' : '#FF669B',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isPure3D ? 'default' : 'pointer',
                                '&:hover': { background: isPure3D ? '#FF669B' : '#FF85B0' },
                                opacity: isPure3D ? 0.5 : 1,
                            }}
                            onClick={(event) => {
                                if (isPure3D) {
                                    event.stopPropagation();
                                    return;
                                }
                                onCustomise(event, item);
                            }}
                        >
                            <img src={customiseIcon} alt="" width={22} height={22} />
                        </Box>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Tooltip title="Add to bundle" arrow>
                            <Box
                                sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: '50%',
                                    background: '#FF669B',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { background: '#FF85B0' },
                                }}
                                onClick={(event) => onAddToBundle(event, item)}
                            >
                                <img src={addToBundleIcon} alt="" width={22} height={22} />
                            </Box>
                        </Tooltip>
                        <Tooltip title="Quick share" arrow>
                            <Box
                                sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: '50%',
                                    background: '#FF669B',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { background: '#FF85B0' },
                                }}
                                onClick={(event) => onQuickShare(event, item)}
                            >
                                <img src={shareIcon} alt="" width={22} height={22} />
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const AlphabetCard = ({ item, imageStore, navigate, isMobile = false, onAddToBundle, onQuickShare, onCustomise }) => {
    const isPure3D = isPure3DModelHandout(item);
    const formatOption = getFormatOption(item);

    return (
        <Box
            onClick={() => navigate(`/handout/${item.contentId}`)}
            sx={{
                cursor: 'pointer',
                background: '#F0F0FC',
                borderRadius: isMobile ? '13px' : '20px',
                border: '1px solid #ECECFA',
                p: isMobile ? '9px' : '14px',
                display: 'grid',
                gridTemplateColumns: isMobile ? '63.06px 1fr' : '96.633px 1fr',
                alignItems: 'stretch',
                gap: isMobile ? '9.38px' : '14px',
                minHeight: isMobile ? '78.96px' : 121,
            }}
        >
            <Box
                sx={{
                    width: isMobile ? '63.06px' : '96.633px',
                    height: isMobile ? '60.69px' : '93px',
                    borderRadius: isMobile ? '6.5px' : '10.28px',
                    background: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                <HandoutImageLoader
                    src={getPreviewSrc(item)}
                    alt={item.contentName}
                    imageStore={imageStore}
                    handoutData={item}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
            </Box>
            <Box
                sx={{
                    minWidth: 0,
                    height: isMobile ? '60.69px' : '93px',
                    display: 'grid',
                    gridTemplateRows: isMobile ? '34px 1.31px 21.1px' : '44px 2px 1fr',
                    rowGap: isMobile ? '2px' : '6px',
                }}
            >
                <Typography
                    sx={{
                        color: '#000453',
                        fontWeight: 600,
                        fontSize: isMobile ? '14px' : '22px',
                        lineHeight: isMobile ? '14px' : '22px',
                        letterSpacing: isMobile ? '-0.28px' : '-0.44px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {item.contentName}
                </Typography>
                <Box
                    sx={{
                        height: isMobile ? '1.31px' : '2px',
                        background: 'rgba(0, 4, 83, 0.10)',
                        borderRadius: '1px',
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        gap: isMobile ? '8px' : '12px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: isMobile ? '3.768px' : '6px',
                            px: isMobile ? '6.783px' : '16px',
                            py: isMobile ? '6.33px' : '8px',
                            borderRadius: isMobile ? '84.406px' : '129.342px',
                            border: isMobile ? '0.377px solid #E5E5E5' : '0.577px solid #E5E5E5',
                            background: '#FFFFFF',
                            color: '#A0A0A0',
                            fontSize: isMobile ? '6.783px' : '10px',
                            lineHeight: isMobile ? '6.783px' : '14px',
                            letterSpacing: isMobile ? '-0.1357px' : 0,
                            minWidth: isMobile ? '56.294px' : 'auto',
                            height: isMobile ? '21.101px' : 'auto',
                            boxSizing: 'border-box',
                        }}
                    >
                        <img
                            src={formatOption.icon}
                            alt=""
                            width={isMobile ? 8.29 : 16}
                            height={isMobile ? 10.55 : 16}
                        />
                        {formatOption.label}
                    </Box>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? '7px' : '6px', mr: 0 }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Tooltip
                            title={
                                isPure3D ? 'Customise unavailable for pure 3D model handouts' : 'Open in customise mode'
                            }
                            arrow
                        >
                            <Box
                                sx={{
                                    width: isMobile ? 20.88 : 32,
                                    height: isMobile ? 20.88 : 32,
                                    borderRadius: '50%',
                                    background: isPure3D ? '#FF669B' : '#FF669B',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: isPure3D ? 'default' : 'pointer',
                                    '&:hover': { background: isPure3D ? '#FF669B' : '#FF85B0' },
                                    opacity: isPure3D ? 0.5 : 1,
                                }}
                                onClick={(event) => {
                                    if (isPure3D) {
                                        event.stopPropagation();
                                        return;
                                    }
                                    onCustomise(event, item);
                                }}
                            >
                                <img
                                    src={customiseIcon}
                                    alt=""
                                    width={isMobile ? 11 : 16}
                                    height={isMobile ? 11 : 16}
                                />
                            </Box>
                        </Tooltip>
                        <Tooltip title="Add to bundle" arrow>
                            <Box
                                onClick={(event) => onAddToBundle(event, item)}
                                sx={{
                                    width: isMobile ? 20.88 : 32,
                                    height: isMobile ? 20.88 : 32,
                                    borderRadius: '50%',
                                    background: '#FF669B',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { background: '#FF85B0' },
                                }}
                            >
                                <img
                                    src={addToBundleIcon}
                                    alt=""
                                    width={isMobile ? 11 : 16}
                                    height={isMobile ? 11 : 16}
                                />
                            </Box>
                        </Tooltip>
                        <Tooltip title="Quick share" arrow>
                            <Box
                                onClick={(event) => onQuickShare(event, item)}
                                sx={{
                                    width: isMobile ? 20.88 : 32,
                                    height: isMobile ? 20.88 : 32,
                                    borderRadius: '50%',
                                    background: '#FF669B',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { background: '#FF85B0' },
                                }}
                            >
                                <img src={shareIcon} alt="" width={isMobile ? 11 : 16} height={isMobile ? 11 : 16} />
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const SearchDropdown = ({ results, onSelect }) => {
    if (!results.length) return null;

    return (
        <Box
            sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 50,
                background: '#FFFFFF',
                borderRadius: '16px',
                border: '1px solid #ECECFA',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                py: '8px',
                zIndex: 20,
            }}
        >
            {results.map((result) => (
                <Box
                    key={result.contentId}
                    onClick={() => onSelect(result)}
                    sx={{
                        px: '16px',
                        py: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRadius: '999px',
                        mx: '10px',
                        cursor: 'pointer',
                        '& .row-arrow': { opacity: 0, transition: 'opacity 0.15s ease' },
                        '&:hover': { backgroundColor: '#F1F1F1' },
                        '&:hover .row-arrow': { opacity: 1 },
                    }}
                >
                    <Typography sx={{ color: '#616063', fontSize: '16px', lineHeight: '20px' }}>
                        {result.contentName}
                    </Typography>
                    <EastIcon className="row-arrow" sx={{ fontSize: 16, color: '#616063' }} />
                </Box>
            ))}
        </Box>
    );
};

export const SearchScreen = observer(() => {
    const { contentStore, searchStore, imageStore, authStore, userDataStore } = useContext(StoreContext);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [selectedFormats, setSelectedFormats] = useState([]);
    const [selectedSpecies, setSelectedSpecies] = useState([]);
    const [selectedConditionCategories, setSelectedConditionCategories] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState({ formats: [], species: [], conditionCategories: [] });
    const [expandedSections, setExpandedSections] = useState({
        format: true,
        species: true,
        conditionCategories: true,
    });
    const [showAllConditionCategories, setShowAllConditionCategories] = useState(false);
    const [activeLetter, setActiveLetter] = useState('A');
    const [alphabetPage, setAlphabetPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(isMobile ? 9 : 8);
    const [recentStartIndex, setRecentStartIndex] = useState(0);
    const [searchDropdownVisible, setSearchDropdownVisible] = useState(false);
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [showSubmittedResults, setShowSubmittedResults] = useState(false);
    const [submittedResultsPage, setSubmittedResultsPage] = useState(1);
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [upgradeCTAClinicOpen, setUpgradeCTAClinicOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarStatus, setSnackbarStatus] = useState('success');
    const [quickShareOpen, setQuickShareOpen] = useState(false);
    const [quickShareHandout, setQuickShareHandout] = useState(null);
    const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
    const itemsPerPageOptions = isMobile ? MOBILE_ITEMS_PER_PAGE_OPTIONS : DESKTOP_ITEMS_PER_PAGE_OPTIONS;
    const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';

    useEffect(() => {
        if (!itemsPerPageOptions.includes(itemsPerPage)) {
            setItemsPerPage(isMobile ? 9 : 8);
            setAlphabetPage(1);
            setSubmittedResultsPage(1);
        }
    }, [isMobile, itemsPerPage, itemsPerPageOptions]);

    useEffect(() => {
        if (location.state?.resetFilters) {
            setSelectedFormats([]);
            setSelectedSpecies([]);
            setSelectedConditionCategories([]);
            setAppliedFilters({ formats: [], species: [], conditionCategories: [] });
            setAppliedSearchTerm('');
            setShowSubmittedResults(false);
            setAlphabetPage(1);
            setSubmittedResultsPage(1);
            searchStore.setRawSearchTerm('');
            return;
        }

        if (location.state?.category === 'Diagrams' || location.state?.filter === 'Diagrams') {
            setSelectedFormats([FORMAT_KEYS.DIAGRAM]);
            setAppliedFilters({ formats: [FORMAT_KEYS.DIAGRAM], species: [], conditionCategories: [] });
        }
        if (location.state?.category === '3D Models' || location.state?.filter === '3D Models') {
            setSelectedFormats([FORMAT_KEYS.MODEL_3D]);
            setAppliedFilters({ formats: [FORMAT_KEYS.MODEL_3D], species: [], conditionCategories: [] });
        }
    }, [location.state, searchStore]);

    useEffect(() => {
        const term = (searchStore.rawSearchTerm || '').trim();
        if (term) {
            setAppliedSearchTerm(term);
            setShowSubmittedResults(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const allContent = useMemo(() => Object.values(contentStore.contentData || {}), [contentStore.contentData]);
    const searchTerm = searchStore.rawSearchTerm || '';
    const isSearchInputActive = searchDropdownVisible || !!searchTerm.trim();
    const normalizedAppliedSearchTerm = normalize(appliedSearchTerm);
    const searchDropdownResults = useMemo(() => {
        const term = searchTerm.trim();
        if (!term || !contentStore?.isInitialized) return [];
        return contentStore.searchContentMeta(term).slice(0, 5);
    }, [searchTerm, contentStore]);

    const formatCounts = useMemo(() => {
        const counts = { [FORMAT_KEYS.HANDOUT]: 0, [FORMAT_KEYS.DIAGRAM]: 0, [FORMAT_KEYS.MODEL_3D]: 0 };
        allContent.forEach((item) => {
            counts[getFormatKey(item)] += 1;
        });
        return counts;
    }, [allContent]);

    const speciesCounts = useMemo(
        () =>
            SPECIES_OPTIONS.reduce((acc, species) => {
                acc[species] = allContent.filter((item) => deepContains(item.categories, species)).length;
                return acc;
            }, {}),
        [allContent],
    );

    const conditionCounts = useMemo(
        () =>
            CONDITION_CATEGORY_OPTIONS.reduce((acc, condition) => {
                acc[condition] = allContent.filter((item) => deepContains(item.categories, condition)).length;
                return acc;
            }, {}),
        [allContent],
    );

    const filteredContent = useMemo(() => {
        return allContent.filter((item) => {
            const formatKey = getFormatKey(item);
            const formatMatch = appliedFilters.formats.length === 0 || appliedFilters.formats.includes(formatKey);
            const speciesMatch =
                appliedFilters.species.length === 0 ||
                appliedFilters.species.some((species) => deepContains(item.categories, species));
            const conditionMatch =
                appliedFilters.conditionCategories.length === 0 ||
                appliedFilters.conditionCategories.some((condition) => deepContains(item.categories, condition));

            const name = normalize(item.contentName);
            const alphabeticalName = normalize(item.alphabeticalName);
            const searchMatch =
                !normalizedAppliedSearchTerm ||
                name.includes(normalizedAppliedSearchTerm) ||
                alphabeticalName.includes(normalizedAppliedSearchTerm);

            return formatMatch && speciesMatch && conditionMatch && searchMatch;
        });
    }, [allContent, appliedFilters, normalizedAppliedSearchTerm]);

    const sortedAlphabeticalContent = useMemo(
        () => [...allContent].sort((a, b) => compareAlphabeticalContent(contentStore, a, b)),
        [allContent, contentStore],
    );

    const groupedAlphabeticalContent = useMemo(() => {
        return sortedAlphabeticalContent.reduce((acc, item) => {
            const sortableName =
                (contentStore.getSortableName ? contentStore.getSortableName(item) : item.contentName) || '';
            const letter = sortableName.charAt(0).toUpperCase();
            if (!letter) return acc;
            if (!acc[letter]) acc[letter] = [];
            acc[letter].push(item);
            return acc;
        }, {});
    }, [contentStore, sortedAlphabeticalContent]);

    const availableLetters = useMemo(
        () => Object.keys(groupedAlphabeticalContent).sort(),
        [groupedAlphabeticalContent],
    );
    useEffect(() => {
        if (!availableLetters.length) {
            setActiveLetter('A');
            return;
        }
        if (!availableLetters.includes(activeLetter)) setActiveLetter(availableLetters[0]);
    }, [availableLetters, activeLetter]);

    const alphabetItems = isMobile ? sortedAlphabeticalContent : groupedAlphabeticalContent[activeLetter] || [];
    const alphabetPageCount = Math.max(1, Math.ceil(alphabetItems.length / itemsPerPage));
    const paginatedAlphabetItems = alphabetItems.slice((alphabetPage - 1) * itemsPerPage, alphabetPage * itemsPerPage);

    const submittedResultsPageCount = Math.max(1, Math.ceil(filteredContent.length / itemsPerPage));
    const paginatedSubmittedResults = filteredContent.slice(
        (submittedResultsPage - 1) * itemsPerPage,
        submittedResultsPage * itemsPerPage,
    );

    const recentHandouts = useMemo(() => {
        const recentSearchNames = Array.isArray(userDataStore?.userData?.recentSearches)
            ? userDataStore.userData.recentSearches
            : [];
        const contentByName = allContent.reduce((acc, item) => {
            acc[item.contentName?.toLowerCase()] = item;
            return acc;
        }, {});
        return recentSearchNames.map((name) => contentByName[name?.toLowerCase()]).filter(Boolean);
    }, [userDataStore?.userData?.recentSearches, allContent]);

    const canMoveRecentLeft = recentStartIndex > 0;
    const recentCardsPerView = isMobile ? MOBILE_RECENT_CARDS_PER_VIEW : RECENT_CARDS_PER_VIEW;
    const canMoveRecentRight = recentStartIndex + recentCardsPerView < recentHandouts.length;
    const visibleRecentHandouts = recentHandouts.slice(recentStartIndex, recentStartIndex + recentCardsPerView);
    const selectedFiltersCount =
        appliedFilters.formats.length + appliedFilters.species.length + appliedFilters.conditionCategories.length;
    const hasActiveFilters =
        appliedFilters.formats.length > 0 ||
        appliedFilters.species.length > 0 ||
        appliedFilters.conditionCategories.length > 0;
    const showResultsView = showSubmittedResults || hasActiveFilters;

    const formatFilterOptions = FORMAT_OPTIONS.map((option) => ({
        label: option.label,
        value: option.key,
        count: formatCounts[option.key] || 0,
    }));
    const speciesFilterOptions = SPECIES_OPTIONS.map((label) => ({
        label,
        value: label,
        count: speciesCounts[label] || 0,
    }));
    const conditionFilterOptions = CONDITION_CATEGORY_OPTIONS.map((label) => ({
        label,
        value: label,
        count: conditionCounts[label] || 0,
    }));

    const toggleSelectedValue = (setter) => (value) => {
        setter((currentValues) =>
            currentValues.includes(value)
                ? currentValues.filter((currentValue) => currentValue !== value)
                : [...currentValues, value],
        );
    };

    const handleApplyFilters = () => {
        setAppliedFilters({
            formats: selectedFormats,
            species: selectedSpecies,
            conditionCategories: selectedConditionCategories,
        });
        setAlphabetPage(1);
        setSubmittedResultsPage(1);
        if (isMobile) setIsMobileFiltersOpen(false);
    };

    const handleSearchInputChange = (value) => {
        searchStore.setRawSearchTerm(value);
        setSearchDropdownVisible(!!value.trim());
        if (!value.trim()) {
            setAppliedSearchTerm('');
            setShowSubmittedResults(false);
            setSubmittedResultsPage(1);
        }
    };

    const handleSearchSubmit = () => {
        const term = searchTerm.trim();
        setAppliedSearchTerm(term);
        setShowSubmittedResults(!!term);
        setSearchDropdownVisible(false);
        setSubmittedResultsPage(1);
    };

    const getRecentSearches = () => {
        if (Array.isArray(userDataStore?.userData?.recentSearches)) {
            return userDataStore.userData.recentSearches;
        }
        return [];
    };

    const persistRecentSearches = async (recentSearches) => {
        userDataStore.setUserData({
            ...userDataStore.userData,
            recentSearches,
        });

        if (!authStore?.idToken) {
            return;
        }

        try {
            if (isMultiUser) {
                if (!userDataStore?.userData?.inviteId || !userDataStore?.userData?.emailAddress) {
                    return;
                }
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, {
                    action: 'updateMultiuser',
                    inviteId: userDataStore.userData.inviteId,
                    emailAddress: userDataStore.userData.emailAddress,
                    recentSearches,
                });
            } else {
                await UserApi.updateClinicianData(authStore.idToken, { recentSearches });
            }
        } catch (error) {
            console.error('Failed to persist recent searches:', error);
        }
    };

    const addRecentSearch = async (searchValue) => {
        const term = (searchValue || '').trim();
        if (!term) {
            return;
        }

        const currentRecentSearches = getRecentSearches();
        const nextRecentSearches = [
            term,
            ...currentRecentSearches.filter((entry) => entry.toLowerCase() !== term.toLowerCase()),
        ].slice(0, RECENT_SEARCHES_LIMIT);

        await persistRecentSearches(nextRecentSearches);
    };

    const handleSearchResultSelect = (result) => {
        setSearchDropdownVisible(false);
        void addRecentSearch(result.contentName);
        navigate(`/handout/${result.contentId}`, { state: { breadcrumbs: ['SEARCH'] } });
    };

    const handleClearFilters = () => {
        setSelectedFormats([]);
        setSelectedSpecies([]);
        setSelectedConditionCategories([]);
        setAppliedFilters({ formats: [], species: [], conditionCategories: [] });
        setAppliedSearchTerm('');
        setShowSubmittedResults(false);
        setAlphabetPage(1);
        setSubmittedResultsPage(1);
        searchStore.setRawSearchTerm('');
    };

    const handleAddToBundle = async (event, handout) => {
        event.stopPropagation();
        const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';

        const upgradeTarget = getHandoutBundleUpgradeTarget(userDataStore?.subscriptionData?.accountType);
        if (upgradeTarget === 'professional') {
            setUpgradeCTAOpen(true);
            return;
        }
        if (upgradeTarget === 'clinic') {
            setUpgradeCTAClinicOpen(true);
            return;
        }

        const userHandoutsJson = userDataStore.userData.handouts || '[]';
        let userHandouts = Array.isArray(userDataStore.userData.handouts)
            ? userDataStore.userData.handouts
            : JSON.parse(userHandoutsJson);

        const existingContentIds = userHandouts.map((entry) => entry.contentId);
        if (existingContentIds.includes(handout.contentId)) {
            setSnackbarMessage('Handout already exists in the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }
        if (userHandouts.length >= 5) {
            setSnackbarMessage('Cannot add more than 5 handouts to the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        userHandouts.push({ contentId: handout.contentId, contentName: handout.contentName, categories: '' });
        const payload = { handouts: userHandouts };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
            userDataStore.updateUserHandouts(userHandouts);
            setSnackbarMessage('Handout added to bundle');
            setSnackbarStatus('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error adding handout to bundle:', error);
            setSnackbarMessage('Failed to add handout to bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
        }
    };

    const handleQuickShare = (event, handout) => {
        event.stopPropagation();
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            setUpgradeCTAOpen(true);
            return;
        }
        setQuickShareHandout(handout);
        setQuickShareOpen(true);
    };

    // Navigate to handout and activate fresh Customise mode (CETRA-738)
    const handleCustomise = (event, handout) => {
        event.stopPropagation();
        localStorage.removeItem('loadCustomisedHandout');
        localStorage.setItem('openInCustomiseMode', 'true');
        navigate(`/handout/${handout.contentId}`);
    };

    const currentPage = showResultsView ? submittedResultsPage : alphabetPage;
    const setCurrentPage = showResultsView ? setSubmittedResultsPage : setAlphabetPage;
    const currentPageCount = showResultsView ? submittedResultsPageCount : alphabetPageCount;
    const hasSubmittedNoResults = showResultsView && filteredContent.length === 0;
    const suggestHandoutPanel = (
        <Box
            sx={{
                width: { xs: '352px', md: '100%' },
                height: { xs: '180px', md: '180px' },
                maxWidth: '100%',
                mx: { xs: 'auto', md: 0 },
                background: '#000453',
                borderRadius: '30px',
                p: 0,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: '10px', md: '16px' },
                pt: { xs: '24px', md: '39px' },
                px: '30px',
                pb: { xs: '24px', md: '39px' },
            }}
        >
            <Box sx={{ maxWidth: { xs: '100%', md: '620px' } }}>
                <Typography
                    sx={{
                        color: '#FFF',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: { xs: '22.99px', md: '32px' },
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: { xs: '21.553px', md: '30px' },
                        letterSpacing: { xs: '-0.92px', md: '-1.28px' },
                        mb: { xs: '8px', md: '10px' },
                    }}
                >
                    Suggest a handout
                </Typography>
                <Typography
                    sx={{
                        color: '#FFF',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: { xs: '14px', md: '20px' },
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: { xs: '20px', md: '28px' },
                        letterSpacing: { xs: '-0.56px', md: '-0.8px' },
                        whiteSpace: { xs: 'normal', md: 'pre-line' },
                    }}
                >
                    {isMobile
                        ? "Have an idea to improve our handouts or topics you'd like to see us cover? We'd love to hear it!"
                        : "Have an idea to improve our handouts or topics\nyou'd like to see us cover? We'd love to hear it!"}
                </Typography>
            </Box>
            <Button
                variant="contained"
                onClick={() => setSuggestDialogOpen(true)}
                endIcon={
                    <Box
                        sx={{
                            width: { xs: '30.8px', md: '39.2px' },
                            height: { xs: '30.8px', md: '39.2px' },
                            borderRadius: '50%',
                            background: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <EastIcon
                            sx={{
                                color: '#FF669B',
                                fontSize: { xs: '23px', md: '26px' },
                                lineHeight: 1,
                            }}
                        />
                    </Box>
                }
                sx={{
                    display: 'inline-flex',
                    height: { xs: '44px', md: '56px' },
                    minHeight: { xs: '44px', md: '56px' },
                    pl: { xs: '17.6px', md: '22.4px' },
                    pr: { xs: '7.04px', md: '8.96px' },
                    py: { xs: '13.2px', md: '16.8px' },
                    gap: '15.806px',
                    borderRadius: '224px',
                    background: '#FF669B',
                    boxSizing: 'border-box',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: { xs: '14px', md: '18px' },
                    lineHeight: { xs: '14px', md: '18px' },
                    letterSpacing: { xs: '-0.28px', md: '-0.36px' },
                    color: '#FFF',
                    textShadow: { xs: '0 0 8.8px rgba(35, 7, 88, 0.10)', md: 'none' },
                    alignSelf: { xs: 'flex-start', md: 'auto' },
                    '&:hover': { background: '#FF85B0' },
                    '& .MuiButton-endIcon': {
                        marginLeft: 0,
                        marginRight: 0,
                        width: { xs: '30.8px', md: '39.2px' },
                        height: { xs: '30.8px', md: '39.2px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                }}
            >
                Make a suggestion
            </Button>
        </Box>
    );

    return (
        <Box
            sx={{
                px: { xs: '16px', md: '20px' },
                pb: { xs: '96px', md: '24px' },
                background: '#F0F0FC',
                minHeight: 'calc(100vh - 140px)',
            }}
        >
            <Box sx={{ maxWidth: '1360px', margin: '0 auto', pt: { xs: '20px', md: '39px' } }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '329px 1fr' },
                        columnGap: { xs: 0, md: '20px' },
                        rowGap: { xs: '20px', md: 0 },
                    }}
                >
                    <Box sx={{ width: { xs: '100%', md: '329px' }, flexShrink: 0 }}>
                        <ClickAwayListener onClickAway={() => setSearchDropdownVisible(false)}>
                            <Box sx={{ position: 'relative' }}>
                                <Box
                                    sx={{
                                        borderRadius: '999px',
                                        background: isSearchInputActive
                                            ? '#FFFFFF'
                                            : 'linear-gradient(176deg, #FF669B 0%, #BD8DE8 110.58%)',
                                        border: isSearchInputActive ? '1px solid #FA96C3' : 'none',
                                        height: 44,
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: '14px',
                                    }}
                                >
                                    <SearchIcon
                                        sx={{ color: isSearchInputActive ? '#616063' : 'white', mr: 1, fontSize: 20 }}
                                    />
                                    <InputBase
                                        value={searchTerm}
                                        placeholder="Search"
                                        onFocus={() => setSearchDropdownVisible(!!searchTerm.trim())}
                                        onChange={(event) => handleSearchInputChange(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault();
                                                handleSearchSubmit();
                                            }
                                        }}
                                        sx={{
                                            color: isSearchInputActive ? '#616063' : 'white',
                                            width: '100%',
                                            height: '100%',
                                            background: 'transparent',
                                            '& .MuiInputBase-input': {
                                                backgroundColor: 'transparent !important',
                                                color: isSearchInputActive ? '#616063' : 'white',
                                                padding: 0,
                                                fontSize: '16px',
                                                lineHeight: '20px',
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                color: isSearchInputActive ? '#A0A0A0' : 'rgba(255,255,255,0.95)',
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                </Box>
                                {searchDropdownVisible && (
                                    <SearchDropdown
                                        results={searchDropdownResults}
                                        onSelect={handleSearchResultSelect}
                                    />
                                )}
                            </Box>
                        </ClickAwayListener>

                        <Box
                            sx={{
                                mt: '21px',
                                width: '329px',
                                background: '#FFFFFF',
                                borderRadius: '30px',
                                p: '30px',
                                border: '1px solid #ECECFA',
                                boxSizing: 'border-box',
                                display: { xs: 'none', md: 'block' },
                            }}
                        >
                            <FilterSection
                                title="Format"
                                options={formatFilterOptions}
                                selectedValues={selectedFormats}
                                onToggleValue={toggleSelectedValue(setSelectedFormats)}
                                expanded={expandedSections.format}
                                onToggleExpanded={() =>
                                    setExpandedSections((current) => ({ ...current, format: !current.format }))
                                }
                            />
                            <Divider sx={{ my: '10px' }} />
                            <FilterSection
                                title="Species"
                                options={speciesFilterOptions}
                                selectedValues={selectedSpecies}
                                onToggleValue={toggleSelectedValue(setSelectedSpecies)}
                                expanded={expandedSections.species}
                                onToggleExpanded={() =>
                                    setExpandedSections((current) => ({ ...current, species: !current.species }))
                                }
                            />
                            <Divider sx={{ my: '10px' }} />
                            <FilterSection
                                title="Condition Categories"
                                options={conditionFilterOptions}
                                selectedValues={selectedConditionCategories}
                                onToggleValue={toggleSelectedValue(setSelectedConditionCategories)}
                                expanded={expandedSections.conditionCategories}
                                onToggleExpanded={() =>
                                    setExpandedSections((current) => ({
                                        ...current,
                                        conditionCategories: !current.conditionCategories,
                                    }))
                                }
                                showAll={showAllConditionCategories}
                                onToggleShowAll={() => setShowAllConditionCategories(true)}
                            />
                            <Box
                                sx={{
                                    mt: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={handleApplyFilters}
                                    endIcon={<img src={arrowRightPinkIcon} alt="" width={39.2} height={39.2} />}
                                    sx={{
                                        borderRadius: '999px',
                                        display: 'inline-flex',
                                        width: '186.36px',
                                        height: '56px',
                                        minHeight: '56px',
                                        py: '16.8px',
                                        px: '16.8px 8.96px 16.8px 22.4px',
                                        boxSizing: 'border-box',
                                        position: 'relative',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: '18px',
                                        fontStyle: 'normal',
                                        lineHeight: '18px',
                                        letterSpacing: '-0.36px',
                                        textShadow: '0 0 11.2px rgba(35, 7, 88, 0.10)',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        background: '#FF669B',
                                        '&:hover': { background: '#FF85B0' },
                                        '& .MuiButton-endIcon': {
                                            margin: 0,
                                            position: 'absolute',
                                            right: '8.96px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: '39.2px',
                                            height: '39.2px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        },
                                    }}
                                >
                                    <Box component="span" sx={{ ml: '4px' }}>
                                        Apply filters
                                    </Box>
                                </Button>
                                <Box
                                    component="button"
                                    onClick={handleClearFilters}
                                    sx={{
                                        border: 'none',
                                        background: 'transparent',
                                        color: '#FF669B',
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: '18px',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        lineHeight: '18px',
                                        letterSpacing: '-0.36px',
                                        textDecorationLine: 'underline',
                                        textDecorationStyle: 'solid',
                                        textDecorationSkipInk: 'none',
                                        textDecorationThickness: 'auto',
                                        textUnderlineOffset: 'auto',
                                        textUnderlinePosition: 'from-font',
                                        cursor: 'pointer',
                                        p: 0,
                                    }}
                                >
                                    Clear
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '14px', md: '16px' } }}>
                        <Box
                            sx={{
                                background: '#FFFFFF',
                                borderRadius: '30px',
                                border: '1px solid #ECECFA',
                                p: { xs: '14px', md: '20px' },
                                alignSelf: hasSubmittedNoResults ? 'start' : 'stretch',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: '10px', mb: '14px', flexWrap: 'wrap' }}>
                                {FORMAT_OPTIONS.map((option) => {
                                    const isActive =
                                        appliedFilters.formats.length === 1 && appliedFilters.formats[0] === option.key;
                                    return (
                                        <Box
                                            key={option.key}
                                            onClick={() => {
                                                const nextFormats =
                                                    appliedFilters.formats.length === 1 &&
                                                    appliedFilters.formats[0] === option.key
                                                        ? []
                                                        : [option.key];
                                                setSelectedFormats(nextFormats);
                                                setAppliedFilters((current) => ({ ...current, formats: nextFormats }));
                                                setSubmittedResultsPage(1);
                                                setAlphabetPage(1);
                                            }}
                                            sx={{
                                                cursor: 'pointer',
                                                border: `1px solid ${isActive ? '#000453' : '#E5E5E5'}`,
                                                borderRadius: '224px',
                                                height: { xs: '33.66px', md: '56px' },
                                                width: { xs: '92.83px', md: 'auto' },
                                                px: { xs: '8px', md: '18px' },
                                                py: { xs: '7px', md: '16.8px' },
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: { xs: '6px', md: '10px' },
                                                background: isActive ? '#000453' : '#FFFFFF',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <img
                                                src={option.icon}
                                                alt={option.label}
                                                width={
                                                    isMobile
                                                        ? Math.max(16, Math.round(option.iconWidth * 0.55))
                                                        : option.iconWidth
                                                }
                                                height={
                                                    isMobile
                                                        ? Math.max(16, Math.round(option.iconHeight * 0.55))
                                                        : option.iconHeight
                                                }
                                            />
                                            <Typography
                                                sx={{
                                                    color: isActive ? '#FFFFFF' : '#616063',
                                                    textShadow: '0 0 11.2px rgba(35, 7, 88, 0.10)',
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontSize: { xs: '12px', md: '18px' },
                                                    fontStyle: 'normal',
                                                    fontWeight: 500,
                                                    lineHeight: { xs: '12px', md: '18px' },
                                                    letterSpacing: { xs: '-0.2px', md: '-0.36px' },
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {option.label}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {showResultsView ? (
                                <>
                                    <Typography
                                        sx={{
                                            color: '#000453',
                                            fontSize: '16px',
                                            lineHeight: '20px',
                                            fontWeight: 600,
                                            mb: '12px',
                                        }}
                                    >
                                        Showing {filteredContent.length} results
                                    </Typography>
                                    {hasSubmittedNoResults && (
                                        <Typography
                                            sx={{
                                                color: '#616063',
                                                fontSize: { xs: '14px', md: '16px' },
                                                lineHeight: { xs: '24px', md: '22px' },
                                                fontWeight: 400,
                                                maxWidth: { xs: '100%', md: '760px' },
                                                mb: '18px',
                                            }}
                                        >
                                            <Box component="span" sx={{ display: 'block' }}>
                                                Sorry, we couldn't find what you're looking for.
                                            </Box>
                                            <Box component="span" sx={{ display: 'block' }}>
                                                We're always adding new content, so please use the button below to
                                                suggest a handout or resource you'd like to see added in the future.
                                            </Box>
                                        </Typography>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                                            gap: '12px',
                                            mb: '14px',
                                        }}
                                    >
                                        {paginatedSubmittedResults.map((item) => (
                                            <FeaturedCard
                                                key={item.contentId}
                                                item={item}
                                                imageStore={imageStore}
                                                navigate={navigate}
                                                onAddToBundle={handleAddToBundle}
                                                onQuickShare={handleQuickShare}
                                                onCustomise={handleCustomise}
                                            />
                                        ))}
                                    </Box>
                                </>
                            ) : (
                                <>
                                    {visibleRecentHandouts.length > 0 && (
                                        <>
                                            <Typography
                                                sx={{
                                                    color: '#000453',
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontSize: { xs: '24px', md: '30px' },
                                                    lineHeight: { xs: '24px', md: '30px' },
                                                    letterSpacing: { xs: '-0.72px', md: '-1.2px' },
                                                    fontWeight: 600,
                                                    mb: '12px',
                                                }}
                                            >
                                                Recently searched
                                            </Typography>
                                            <Box sx={{ position: 'relative', mb: '18px' }}>
                                                <IconButton
                                                    onClick={() =>
                                                        canMoveRecentLeft && setRecentStartIndex((value) => value - 1)
                                                    }
                                                    sx={{
                                                        position: 'absolute',
                                                        left: { xs: -12, md: -20 },
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        zIndex: 2,
                                                        width: 50,
                                                        height: 50,
                                                        background: canMoveRecentLeft ? '#FF669B' : '#FFC1D7',
                                                        '&:hover': {
                                                            background: canMoveRecentLeft ? '#FF85B0' : '#FFC1D7',
                                                        },
                                                    }}
                                                >
                                                    <img
                                                        src={arrowRightIcon}
                                                        alt="Previous"
                                                        width={18}
                                                        style={{ transform: 'scaleX(-1)' }}
                                                    />
                                                </IconButton>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 314px)' },
                                                        columnGap: { xs: '12px', md: '15px' },
                                                        rowGap: { xs: '12px', md: 0 },
                                                        width: { xs: '100%', md: '972px' },
                                                        mx: { xs: 0, md: 'auto' },
                                                        justifyContent: 'start',
                                                    }}
                                                >
                                                    {visibleRecentHandouts.map((item) => (
                                                        <FeaturedCard
                                                            key={item.contentId}
                                                            item={item}
                                                            imageStore={imageStore}
                                                            navigate={navigate}
                                                            onAddToBundle={handleAddToBundle}
                                                            onQuickShare={handleQuickShare}
                                                            onCustomise={handleCustomise}
                                                        />
                                                    ))}
                                                </Box>
                                                <IconButton
                                                    onClick={() =>
                                                        canMoveRecentRight && setRecentStartIndex((value) => value + 1)
                                                    }
                                                    sx={{
                                                        position: 'absolute',
                                                        right: { xs: -12, md: -20 },
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        zIndex: 2,
                                                        width: 50,
                                                        height: 50,
                                                        background: canMoveRecentRight ? '#FF669B' : '#FFC1D7',
                                                        '&:hover': {
                                                            background: canMoveRecentRight ? '#FF85B0' : '#FFC1D7',
                                                        },
                                                    }}
                                                >
                                                    <img src={arrowRightIcon} alt="Next" width={18} />
                                                </IconButton>
                                            </Box>
                                        </>
                                    )}

                                    <Typography
                                        sx={{
                                            color: '#000453',
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: { xs: '24px', md: '30px' },
                                            lineHeight: { xs: '24px', md: '30px' },
                                            letterSpacing: { xs: '-0.72px', md: '-1.2px' },
                                            fontWeight: 600,
                                            mb: '12px',
                                        }}
                                    >
                                        All handouts (A-Z)
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: { xs: 'none', md: 'grid' },
                                            gridTemplateColumns: 'repeat(26, minmax(0, 1fr))',
                                            width: '100%',
                                            border: '1.2px solid rgba(0, 4, 83, 0.16)',
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            mb: '25px',
                                        }}
                                    >
                                        {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => {
                                            const available = availableLetters.includes(letter);
                                            const active = activeLetter === letter;
                                            const isFirst = letter === 'A';
                                            const isLast = letter === 'Z';
                                            return (
                                                <Box
                                                    key={letter}
                                                    onClick={() => {
                                                        if (!available) return;
                                                        setActiveLetter(letter);
                                                        setAlphabetPage(1);
                                                    }}
                                                    sx={{
                                                        minHeight: 36,
                                                        background: active ? '#000453' : '#F0F0FC',
                                                        color: active ? '#FFFFFF' : available ? '#000453' : '#A4A4C2',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '16px',
                                                        lineHeight: '36px',
                                                        fontWeight: 500,
                                                        cursor: available ? 'pointer' : 'default',
                                                        borderRight: isLast
                                                            ? 'none'
                                                            : '1.2px solid rgba(0, 4, 83, 0.16)',
                                                        borderTopLeftRadius: isFirst ? '10px' : 0,
                                                        borderBottomLeftRadius: isFirst ? '10px' : 0,
                                                        borderTopRightRadius: isLast ? '10px' : 0,
                                                        borderBottomRightRadius: isLast ? '10px' : 0,
                                                    }}
                                                >
                                                    {letter}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    {paginatedAlphabetItems.length === 0 ? (
                                        <Typography sx={{ color: '#66668A', fontSize: '16px' }}>
                                            No handouts found for the current filters.
                                        </Typography>
                                    ) : (
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                                gap: '10px',
                                                mb: '14px',
                                            }}
                                        >
                                            {paginatedAlphabetItems.map((item) => (
                                                <AlphabetCard
                                                    key={item.contentId}
                                                    item={item}
                                                    imageStore={imageStore}
                                                    navigate={navigate}
                                                    isMobile={isMobile}
                                                    onAddToBundle={handleAddToBundle}
                                                    onQuickShare={handleQuickShare}
                                                    onCustomise={handleCustomise}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </>
                            )}

                            <Box
                                sx={{
                                    mt: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <IconButton
                                        size="small"
                                        disabled={currentPage <= 1}
                                        onClick={() => setCurrentPage((value) => Math.max(1, value - 1))}
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '42px',
                                            border: '1.313px solid #E5E5E5',
                                            background: '#FFFFFF',
                                            color: '#6D6D6D',
                                            '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                        }}
                                    >
                                        <ChevronLeftIcon sx={{ fontSize: '15.8px' }} />
                                    </IconButton>
                                    {getPaginationItems(currentPage, currentPageCount).map((paginationItem) => {
                                        if (typeof paginationItem === 'string') {
                                            return (
                                                <Box
                                                    key={paginationItem}
                                                    sx={{
                                                        width: 24,
                                                        height: 42,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#616063',
                                                        fontSize: '16px',
                                                        lineHeight: '16px',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    ...
                                                </Box>
                                            );
                                        }

                                        const pageNumber = paginationItem;
                                        const isActive = pageNumber === currentPage;
                                        return (
                                            <Box
                                                key={pageNumber}
                                                onClick={() => setCurrentPage(pageNumber)}
                                                sx={{
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: '42px',
                                                    border: isActive ? 'none' : '1.313px solid #E5E5E5',
                                                    background: isActive ? '#FF669B' : '#FFFFFF',
                                                    color: isActive ? '#FFFFFF' : '#616063',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '16px',
                                                    lineHeight: '16px',
                                                    fontWeight: isActive ? 400 : 500,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {pageNumber}
                                            </Box>
                                        );
                                    })}
                                    <IconButton
                                        size="small"
                                        disabled={currentPage >= currentPageCount}
                                        onClick={() => setCurrentPage((value) => Math.min(currentPageCount, value + 1))}
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '42px',
                                            border: '1.313px solid #E5E5E5',
                                            background: '#FFFFFF',
                                            color: '#6D6D6D',
                                            '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                        }}
                                    >
                                        <ChevronRightIcon sx={{ fontSize: '15.8px' }} />
                                    </IconButton>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Typography
                                        sx={{
                                            color: '#616063',
                                            fontSize: { xs: '16px', md: '13px' },
                                            lineHeight: { xs: '16px', md: '13px' },
                                        }}
                                    >
                                        Items per page
                                    </Typography>
                                    <Select
                                        size="small"
                                        value={itemsPerPage}
                                        onChange={(event) => {
                                            setItemsPerPage(Number(event.target.value));
                                            setAlphabetPage(1);
                                            setSubmittedResultsPage(1);
                                        }}
                                        sx={{
                                            width: { xs: 63, md: 'auto' },
                                            height: { xs: 30, md: 28 },
                                            borderRadius: { xs: '10px', md: '999px' },
                                            fontSize: { xs: '16px', md: '13px' },
                                            color: '#616063',
                                            '& .MuiSelect-select': {
                                                py: 0,
                                                minHeight: 'unset',
                                                lineHeight: { xs: '16px', md: '13px' },
                                                pl: { xs: '15px', md: '10px' },
                                                pr: { xs: '26px', md: '24px' },
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#E5E5E5',
                                                borderWidth: '1px',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#E5E5E5',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#E5E5E5',
                                            },
                                            '& .MuiSelect-icon': {
                                                right: { xs: '8px', md: '8px' },
                                                color: '#616063',
                                            },
                                        }}
                                    >
                                        {itemsPerPageOptions.map((value) => (
                                            <MenuItem key={value} value={value}>
                                                {value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                        </Box>
                        {suggestHandoutPanel}
                    </Box>
                </Box>
            </Box>
            <Drawer
                anchor="bottom"
                open={isMobile && isMobileFiltersOpen}
                onClose={() => setIsMobileFiltersOpen(false)}
                PaperProps={{
                    sx: {
                        background: 'transparent',
                        boxShadow: 'none',
                        p: 0,
                    },
                }}
            >
                <Box sx={{ px: '16px', pb: '82px' }}>
                    <Box
                        sx={{
                            background: '#FFFFFF',
                            borderRadius: '20px',
                            p: 0,
                            border: '1px solid #ECECFA',
                            boxSizing: 'border-box',
                            maxHeight: '82vh',
                            minHeight: '420px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                p: '12px 14px 0',
                            }}
                        >
                            <FilterSection
                                title="Format"
                                options={formatFilterOptions}
                                selectedValues={selectedFormats}
                                onToggleValue={toggleSelectedValue(setSelectedFormats)}
                                expanded={expandedSections.format}
                                onToggleExpanded={() =>
                                    setExpandedSections((current) => ({ ...current, format: !current.format }))
                                }
                            />
                            <Divider sx={{ my: '10px' }} />
                            <FilterSection
                                title="Species"
                                options={speciesFilterOptions}
                                selectedValues={selectedSpecies}
                                onToggleValue={toggleSelectedValue(setSelectedSpecies)}
                                expanded={expandedSections.species}
                                onToggleExpanded={() =>
                                    setExpandedSections((current) => ({ ...current, species: !current.species }))
                                }
                            />
                            <Divider sx={{ my: '10px' }} />
                            <FilterSection
                                title="Condition Categories"
                                options={conditionFilterOptions}
                                selectedValues={selectedConditionCategories}
                                onToggleValue={toggleSelectedValue(setSelectedConditionCategories)}
                                expanded={expandedSections.conditionCategories}
                                onToggleExpanded={() =>
                                    setExpandedSections((current) => ({
                                        ...current,
                                        conditionCategories: !current.conditionCategories,
                                    }))
                                }
                                showAll={showAllConditionCategories}
                                onToggleShowAll={() => setShowAllConditionCategories(true)}
                            />
                        </Box>
                        <Box
                            sx={{
                                borderTop: '1px solid #ECECFA',
                                p: '12px 14px 14px',
                                background: '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                endIcon={<img src={arrowRightPinkIcon} alt="" width={41.94} height={41.94} />}
                                sx={{
                                    borderRadius: '999px',
                                    display: 'inline-flex',
                                    width: '181.404px',
                                    height: '59.915px',
                                    minHeight: '59.915px',
                                    py: '8.987px',
                                    px: '8.987px 9.586px 8.987px 23.966px',
                                    boxSizing: 'border-box',
                                    position: 'relative',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '16px',
                                    lineHeight: '16px',
                                    letterSpacing: '-0.32px',
                                    textShadow: '0 0 11.983px rgba(35, 7, 88, 0.10)',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    background: '#FF669B',
                                    '&:hover': { background: '#FF85B0' },
                                    '& .MuiButton-endIcon': {
                                        margin: 0,
                                        position: 'absolute',
                                        right: '9.586px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '41.94px',
                                        height: '41.94px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    },
                                }}
                            >
                                Apply filters
                            </Button>
                            <Box
                                component="button"
                                onClick={handleClearFilters}
                                sx={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#FF669B',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '18px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '18px',
                                    letterSpacing: '-0.36px',
                                    textDecorationLine: 'underline',
                                    textDecorationStyle: 'solid',
                                    textDecorationSkipInk: 'none',
                                    textDecorationThickness: 'auto',
                                    textUnderlineOffset: 'auto',
                                    textUnderlinePosition: 'from-font',
                                    cursor: 'pointer',
                                    p: 0,
                                }}
                            >
                                Clear
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Drawer>
            {isMobile && (
                <Box
                    sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1200,
                        background: '#FFFFFF',
                        borderTop: '1px solid #ECECFA',
                        height: '85px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Button
                        onClick={() => setIsMobileFiltersOpen((value) => !value)}
                        startIcon={<img src={filtersIcon} alt="" width={15} height={15} />}
                        sx={{
                            borderRadius: '999px',
                            width: '136px',
                            height: '46.66px',
                            minWidth: '136px',
                            textTransform: 'none',
                            color: '#FFFFFF',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: '16.633px',
                            lineHeight: '16.633px',
                            letterSpacing: '-0.333px',
                            textShadow: '0 0 9.334px rgba(35, 7, 88, 0.10)',
                            background: isMobileFiltersOpen || selectedFiltersCount > 0 ? '#000453' : '#FF669B',
                            '&:hover': {
                                background: isMobileFiltersOpen || selectedFiltersCount > 0 ? '#000453' : '#FF85B0',
                            },
                            '& .MuiButton-startIcon': {
                                marginRight: '6px',
                                marginLeft: 0,
                            },
                        }}
                    >
                        {isMobileFiltersOpen || selectedFiltersCount > 0
                            ? `Filters (${selectedFiltersCount})`
                            : 'Filters'}
                    </Button>
                </Box>
            )}
            <CTAUpgradeDialog
                open={upgradeCTAClinicOpen}
                handleClose={() => setUpgradeCTAClinicOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Upgrade now for clinic-wide access and advanced features!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to add more users and enjoy full access to our features!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                    'Bundling of handouts and diagrams for seamless sharing',
                    'Personalise with your clinic logo',
                ]}
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => setUpgradeCTAOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock unlimited sharing and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts',
                    'Unlimited digital sharing of all anatomy diagrams',
                    'Personalise with your clinic details',
                ]}
                imageUrl={getCTAUpgradeImageUrl('professional')}
            />

            {quickShareHandout && (
                <QuickShareDialog
                    open={quickShareOpen}
                    handout={quickShareHandout}
                    onClose={() => {
                        setQuickShareOpen(false);
                        setQuickShareHandout(null);
                    }}
                />
            )}

            <SuggestHandoutDialog open={suggestDialogOpen} onClose={() => setSuggestDialogOpen(false)} />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={
                    <Box display="flex" alignItems="center">
                        {snackbarMessage}
                        {snackbarStatus === 'success' ? (
                            <CheckIcon sx={{ ml: 1, color: 'white' }} />
                        ) : (
                            <CloseIcon sx={{ ml: 1, color: 'red' }} />
                        )}
                    </Box>
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundImage: 'linear-gradient(132deg, #FA96C3 -2.07%, #FF669B 15.82%, #BD8DE8 116.64%)',
                        display: 'flex',
                        justifyContent: 'center',
                        borderRadius: 2,
                    },
                }}
            />
        </Box>
    );
});
